/**
 * What an invoice still owes, and whether it is late. Defined once.
 *
 * Three screens had three answers, and all of them were reachable from the
 * same desk on the same afternoon:
 *
 *   Payments      summed invoice totals for anything not paid or cancelled,
 *                 so money already banked against a partly-paid invoice was
 *                 counted as still owed, and drafts counted as owed at all.
 *                 It read $721K against a real $657,844.
 *   Invoices      filtered on the stored `status` string: "Overdue" matched
 *                 only status === 'overdue', and "Outstanding" only
 *                 status === 'sent'. Of three invoices past due, Overdue
 *                 found one and Outstanding found a different one. An agent
 *                 chasing late money would have missed $395,344.
 *   Revenue &     aged by balance_due with its own inline fallback.
 *   fees
 *
 * The rule below is the money, not a label somebody has to remember to
 * update. A status is a thing a human sets; a balance and a due date are
 * facts. Where they disagree, the facts win.
 */

import { parseDateOnly, startOfLocalDay } from './formatters'

/**
 * Statuses on which nothing is owed: settled, void, or never issued.
 *
 * `draft` belongs here. It is not money somebody owes the agency, it is a
 * note to self that has not been sent.
 */
export const NOTHING_OWED_STATUSES = new Set(['paid', 'cancelled', 'draft'])

type InvoiceLike = {
  status?: string | null
  total?: number | null
  /** Legacy alias the API mapper fills from `total`. */
  total_amount?: number | null
  amount_paid?: number | null
  balance_due?: number | null
  due_date?: string | null
}

/**
 * What is still owed on this invoice.
 *
 * `balance_due` is written by the payment recalculation and is authoritative
 * when present. An invoice raised before that existed carries none, and then
 * what is open is the total less whatever has been paid — never the total on
 * its own, which is the error this file exists to stop.
 */
export function invoiceOpenBalance(invoice: InvoiceLike): number {
  const stored = Number(invoice.balance_due)
  if (invoice.balance_due != null && Number.isFinite(stored)) return stored
  const total = Number(invoice.total ?? invoice.total_amount)
  if (!Number.isFinite(total)) return 0
  return total - (Number(invoice.amount_paid) || 0)
}

/** True when this invoice still owes the agency money. */
export function isInvoiceOwed(invoice: InvoiceLike): boolean {
  if (NOTHING_OWED_STATUSES.has(String(invoice.status))) return false
  return invoiceOpenBalance(invoice) > 0
}

/**
 * True when money is owed and the due date has gone.
 *
 * Read from the date rather than the status, because the status only says
 * "overdue" if somebody changed it. Every invoice here that was genuinely
 * late was carrying `sent` or `partial`.
 */
export function isInvoiceOverdue(invoice: InvoiceLike, now: Date = new Date()): boolean {
  if (!isInvoiceOwed(invoice)) return false
  const due = parseDateOnly(invoice.due_date)
  if (!due) return false
  return startOfLocalDay(due).getTime() < startOfLocalDay(now).getTime()
}

/** Total still owed across a set of invoices. */
export function totalOwed(invoices: InvoiceLike[]): number {
  return invoices.filter(isInvoiceOwed).reduce((sum, i) => sum + invoiceOpenBalance(i), 0)
}
